/**
 * SectionOriginalReader — side panel showing the original PDF page(s)
 * for a section.
 *
 * Uses the cached /api/v2/rfp/{id}/page/{p}/image endpoint (JPEG,
 * GIN-cached on disk, ~one-tenth the payload of base64 PNGs from the
 * v1 /pages-range endpoint). Auth attaches via a short-lived ?token=
 * minted from /file-token; <img src> can't carry an Authorization
 * header so the token is the only way through.
 *
 * Props:
 *   rfpId       string — required.
 *   open        bool   — controls Sheet visibility.
 *   onOpenChange(bool) — Radix open-state callback.
 *   pageStart   number — 1-indexed start (the caller should already
 *                        have converted from 0-indexed source data).
 *   pageEnd     number — 1-indexed end (inclusive).
 *   sectionTitle string — for the Sheet header.
 *
 * Behaviour:
 *   * Shows one image per page in the requested range.
 *   * Caps the visible range at 6 pages to keep memory + DOM lean —
 *     huge ranges (whole-document sections from a busted parse) fall
 *     back to a slim navigator that lets the user step through.
 *   * Soft errors per image (broken source files, missing pages) show
 *     a placeholder with the page number so the user knows what's
 *     missing instead of a blank gap.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Loader2, AlertTriangle, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { v2Api } from "@/lib/v2Api";


const MAX_VISIBLE_PAGES = 6;


export default function SectionOriginalReader({
  rfpId, open, onOpenChange, pageStart, pageEnd, sectionTitle,
}) {
  const [token, setToken] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [windowStart, setWindowStart] = useState(null);

  const start = Math.max(1, Number(pageStart) || 1);
  const end = Math.max(start, Number(pageEnd) || start);
  const totalPages = end - start + 1;

  // Mint the file token on each open. The token has a short TTL (5 min
  // in the backend); subsequent re-opens just re-mint rather than
  // trying to refresh in-flight.
  useEffect(() => {
    if (!open || !rfpId) return;
    let cancelled = false;
    setTokenLoading(true);
    setTokenError(null);
    setWindowStart(start);
    v2Api.rfp.fileToken(rfpId)
      .then((tk) => { if (!cancelled) setToken(tk); })
      .catch((err) => {
        if (cancelled) return;
        setToken(null);
        const detail = err?.response?.data?.detail ?? err?.message ?? "Could not load original";
        setTokenError(detail);
        toast.error(detail);
      })
      .finally(() => { if (!cancelled) setTokenLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rfpId]);

  const visibleRange = useMemo(() => {
    if (windowStart == null) return [];
    const winEnd = Math.min(end, windowStart + MAX_VISIBLE_PAGES - 1);
    const out = [];
    for (let p = windowStart; p <= winEnd; p += 1) out.push(p);
    return out;
  }, [windowStart, end]);

  const canPagePrev = windowStart != null && windowStart > start;
  const canPageNext = windowStart != null && (windowStart + MAX_VISIBLE_PAGES) <= end;

  const openFullDocument = async () => {
    try {
      await v2Api.rfp.openFileInNewTab(rfpId, start);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ?? err?.message ?? "Couldn't open the PDF"
      );
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col p-0">
        <SheetHeader className="px-4 pt-4 pb-3 border-b">
          <SheetTitle className="text-base font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" />
            Original — {sectionTitle || "section"}
          </SheetTitle>
          <SheetDescription className="text-xs text-slate-500">
            Pages {start}–{end} of the source PDF
            {totalPages > MAX_VISIBLE_PAGES
              ? ` · showing ${MAX_VISIBLE_PAGES} at a time`
              : ""}
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 py-3 border-b flex items-center gap-2">
          <Button
            size="sm" variant="outline"
            disabled={!canPagePrev}
            onClick={() => setWindowStart((s) => Math.max(start, (s ?? start) - MAX_VISIBLE_PAGES))}
            className="h-7 text-xs"
          >
            ← Earlier
          </Button>
          <Button
            size="sm" variant="outline"
            disabled={!canPageNext}
            onClick={() => setWindowStart((s) => Math.min(end, (s ?? start) + MAX_VISIBLE_PAGES))}
            className="h-7 text-xs"
          >
            Later →
          </Button>
          <Button
            size="sm" variant="ghost"
            onClick={openFullDocument}
            className="ml-auto h-7 text-xs gap-1"
            title="Open the full PDF in a new tab"
          >
            <ExternalLink className="w-3 h-3" /> Full PDF
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {tokenLoading && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing access…
            </div>
          )}
          {tokenError && (
            <div className="flex items-center gap-2 text-sm text-rose-700">
              <AlertTriangle className="w-4 h-4" /> {tokenError}
            </div>
          )}
          {!tokenLoading && !tokenError && token && visibleRange.map((p) => (
            <PageImage
              key={p} rfpId={rfpId} page={p} token={token}
            />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}


function PageImage({ rfpId, page, token }) {
  const [errored, setErrored] = useState(false);
  const src = v2Api.rfp.pageImageUrl(rfpId, page, token);
  return (
    <figure className="border rounded-md overflow-hidden bg-white">
      <figcaption className="px-3 py-1.5 text-[11px] text-slate-500 border-b bg-slate-50">
        Page {page}
      </figcaption>
      {errored ? (
        <div className="p-6 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" />
          Couldn't render page {page} — the source file may be missing this page.
        </div>
      ) : (
        <img
          src={src}
          alt={`Page ${page}`}
          loading="lazy"
          onError={() => setErrored(true)}
          className="w-full block"
        />
      )}
    </figure>
  );
}
